import genericAvatar from "assets/images/generic_avatar.jpg";
import { ErrorModal, PhotoUploader } from "components";
import { getDownloadURL } from "firebase/storage";
import { CuratedTeacher, emptyTeacher } from "models";
import React, { useState } from "react";
import { Button, Col, Form, Modal } from "react-bootstrap";
import Img from "react-cool-img";
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from "react-places-autocomplete";
import { addStudentTeacherStory, updateStudentTeacherStory, uploadProfilePhoto } from "services";
import { generateFullName } from "utilities";

interface AddEditStudentTeacherModalProps {
  /** Whether the modal should be shown. */
  show: boolean;

  setShow: React.Dispatch<React.SetStateAction<boolean>>;

  /** Function to be called when canceling or closing the modal. */
  /** The alum to edit, or undefined if creating a new alum. */
  currentTeacher: CuratedTeacher | undefined;
}

/** Modal for adding/editing curated alumni. */
export function AddEditStudentTeacherModal({
  show,
  setShow,
  currentTeacher,
}: AddEditStudentTeacherModalProps): JSX.Element {
  const [editedTeacher, setEditedTeacher] = useState<CuratedTeacher>(
    currentTeacher ? { ...currentTeacher } : { ...emptyTeacher },
  );

  /**
   * A function that will return the address/location of the current teacher, or
   * an empty string if null
   * @returns The location of the current (unedited) teacher, or an empty string if it is a new teacher
   */
  function setTeacherAddress() {
    if (currentTeacher) {
      return currentTeacher.schoolAddress;
    }
    return "";
  }

  // const [isShowingNewTeacherModal, setIsShowingNewTeacherModal] = useState(show);
  const [validated, setValidated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadedPhoto, setUploadedPhoto] = useState<File | undefined>(undefined);
  const [isReplacingPhoto, setIsReplacingPhoto] = useState(false);
  const isNew = currentTeacher === undefined;
  const [error, setError] = useState<Error | undefined>(undefined);
  const [address, setAddress] = useState(setTeacherAddress());
  editedTeacher.schoolAddress = address;
  editedTeacher.type = "student-teacher";
  const [coordinates, setCoordinates] = React.useState({
    lat: 42.979167,
    lng: -85.808891,
  });
  editedTeacher.longitude = coordinates.lng;
  editedTeacher.latitude = coordinates.lat;

  const handleSelect = async (mapvalue: string) => {
    const results = await geocodeByAddress(mapvalue);
    const latLng = await getLatLng(results[0]);
    setAddress(mapvalue);
    setCoordinates(latLng);
  };

  const handleClose = () => {
    setShow(false);
  };

  function GradYearOrFacultySince() {
    return "Grad Year";
  }

  /**
   * This method will handle the submit function
   * First checking if the admin has submitted all the required fields
   * Then upload the photo to firebase storage and upload photo url to firestore with the profile
   * Finally upload the profile to firestore
   */
  async function submitTeacher(event: {
    currentTarget: any;
    preventDefault: () => void;
    stopPropagation: () => void;
  }) {
    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      event.preventDefault();
      event.stopPropagation();
    } else {
      event.preventDefault(); // prevents the page from reloading immediately on submit

      if (uploadedPhoto !== undefined) {
        const uploadTask = uploadProfilePhoto(uploadedPhoto, editedTeacher);
        await uploadTask;
        const url = await getDownloadURL(uploadTask.snapshot.ref);
        editedTeacher.photoUrl = url;
      }

      const submitFn: () => Promise<unknown> = isNew
        ? () => addStudentTeacherStory(editedTeacher)
        : () => updateStudentTeacherStory(editedTeacher.id, editedTeacher);

      submitFn()
        .catch(err => setError(err))
        .finally(() => {
          window.location.reload(); // this is not the best but my window was reloading so hard with historuy.push
        });
      setIsSubmitting(true);
    }
    setValidated(true);
    // window.location.reload();
  }

  return (
    <>
      <Modal show={show} size="lg" className="add-edit-alumni-modal">
        <Form noValidate validated={validated} onSubmit={submitTeacher}>
          <Modal.Header closeButton={false}>
            <Modal.Title>
              {isNew
                ? "Add New Student Teacher"
                : `Edit ${generateFullName(editedTeacher.firstName, editedTeacher.lastName)}`}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group>
              <strong>Basic Info</strong>
            </Form.Group>
            <Form.Row>
              <Col>
                <TeacherFormGroup
                  attribute="firstName"
                  label="First Name"
                  placeholder="e.g. Jane"
                  onChange={e =>
                    setEditedTeacher({ ...editedTeacher, firstName: e.target.value.trim() })
                  }
                  defaultValue={editedTeacher.firstName}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  Please provide a valid first name.
                </Form.Control.Feedback>
              </Col>

              <Col>
                <TeacherFormGroup
                  attribute="lastName"
                  label="Last Name"
                  placeholder="e.g. Doe"
                  onChange={e =>
                    setEditedTeacher({ ...editedTeacher, lastName: e.target.value.trim() })
                  }
                  defaultValue={editedTeacher.lastName}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  Please provide a valid last name.
                </Form.Control.Feedback>
              </Col>
            </Form.Row>
            <Form.Row>
              <Col>
                <Form.Label>
                  Major
                  <span className="text-danger"> *</span>
                </Form.Label>
                <Form.Control
                  as="select"
                  value={editedTeacher.major}
                  onChange={e => {
                    setEditedTeacher({ ...editedTeacher, major: e.target.value });
                  }}
                  required
                >
                  <option value=""> Select Option</option>
                  <option value="elementary"> Elementary</option>
                  <option value="secondary">Secondary</option>
                  <option value="special-education">Special-Education</option>
                </Form.Control>
                <Form.Control.Feedback type="invalid">Please select a Major.</Form.Control.Feedback>
              </Col>
              <Col>
                <TeacherFormGroup
                  type="number"
                  attribute="gradYear"
                  label={GradYearOrFacultySince()}
                  placeholder={`e.g. ${new Date().getFullYear()}`}
                  onChange={e =>
                    setEditedTeacher({ ...editedTeacher, gradYear: parseInt(e.target.value, 10) })
                  }
                  defaultValue={editedTeacher.gradYear ? editedTeacher.gradYear.toString() : ""}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  Please provide a valid graduation year.
                </Form.Control.Feedback>
              </Col>
            </Form.Row>
            <Form.Group>
              <Form.Label>Profile Photo</Form.Label>
              {!!editedTeacher.photoUrl && !isReplacingPhoto ? (
                <Form.Row>
                  <Col xs={2}>
                    <Img
                      src={editedTeacher.photoUrl || ""}
                      placeholder={genericAvatar}
                      alt={`${editedTeacher.firstName} ${editedTeacher.lastName}`}
                      width="100%"
                      loading="lazy"
                      className="img-circle"
                    />
                  </Col>
                  <Col className="d-flex align-items-center">
                    <Button variant="outline-secondary" onClick={() => setIsReplacingPhoto(true)}>
                      Replace
                    </Button>
                  </Col>
                </Form.Row>
              ) : (
                <PhotoUploader onDrop={(photos: File[]) => setUploadedPhoto(photos[0])} />
              )}
            </Form.Group>

            <Form.Row>
              <Col>
                <TeacherFormGroup
                  attribute="schoolName"
                  label="School Name"
                  placeholder="e.g. West Ottawa High School"
                  onChange={e =>
                    setEditedTeacher({ ...editedTeacher, schoolName: e.target.value.trim() })
                  }
                  defaultValue={editedTeacher.schoolName || ""}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  Please provide a valid School Name.
                </Form.Control.Feedback>
              </Col>

              <Col>
                <Form.Label>
                  School Address
                  <span className="text-danger"> *</span>
                </Form.Label>

                <PlacesAutocomplete value={address} onChange={setAddress} onSelect={handleSelect}>
                  {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                    <div>
                      <Form.Control
                        {...getInputProps({
                          placeholder:
                            "e.g. West Ottawa High School, 3685 Butternut Dr. Holland, MI 49424",
                        })}
                        required
                      />
                      <div>
                        {loading ? <div>...loading</div> : null}

                        {suggestions.map(suggestion => {
                          const style = {
                            backgroundColor: suggestion.active ? "#41b6e6" : "#fff",
                          };

                          return (
                            <div {...getSuggestionItemProps(suggestion, { style })}>
                              {suggestion.description}
                            </div>
                          );
                        })}
                      </div>
                      <Form.Control.Feedback type="invalid">
                        Please provide a valid School Address.
                      </Form.Control.Feedback>
                    </div>
                  )}
                </PlacesAutocomplete>
              </Col>

              <Col>
                <TeacherFormGroup
                  attribute="district"
                  label="District"
                  placeholder="e.g. West Ottawa Public School District"
                  onChange={e =>
                    setEditedTeacher({ ...editedTeacher, district: e.target.value.trim() })
                  }
                  defaultValue={editedTeacher.district || ""}
                />
              </Col>
            </Form.Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button variant="outline-primary" type="submit" disabled={isSubmitting}>
              Submit
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
      <ErrorModal error={error} />
    </>
  );
}

interface TeacherFormGroupProps {
  attribute: keyof CuratedTeacher;
  type?: "text" | "number";
  as?: "textarea";
  label: string;
  placeholder: string;
  onChange: React.ChangeEventHandler<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>;
  defaultValue: string;
  required?: boolean;
  rows?: number;
}

function TeacherFormGroup({
  attribute,
  label,
  placeholder,
  onChange,
  defaultValue,
  type,
  as,
  required,
  rows,
}: TeacherFormGroupProps) {
  return (
    <Form.Group controlId={attribute}>
      <Form.Label>
        {label}
        {required && <span className="text-danger"> *</span>}
      </Form.Label>
      <Form.Control
        placeholder={placeholder}
        onChange={onChange}
        type={type || "text"}
        defaultValue={defaultValue}
        as={as}
        required={required}
        rows={rows}
      />
    </Form.Group>
  );
}
